"use client";

/**
 * Read-only formula trace for a report figure, metric or value stream.
 *
 * Ported from the design source `compass-oppreport.jsx` (`DrillModal`, and the
 * `.rv-m*` / `.rv-fx*` / `.rv-tok*` / `.rv-d*` / `.rv-tl*` rules in `RV_CSS`) in
 * Claude Design project `79357508-fe09-4122-831f-a0014c6a31b0`.
 *
 * Editing never happens here — the report is read-only. Every escape hatch is
 * the generic `onOpenAssumptions` link, which switches the sidebar to the
 * existing assumptions tab (plan §1). `onOpenAssumption` is reserved for a
 * future focused-row deep link and is deliberately not wired.
 *
 * Two root shapes:
 *
 * - **Formula** (`target.formula !== null` and the target is not an aggregate) —
 *   the design's tokenised row. Operand tokens are buttons carrying the node's
 *   label and value; operators render as plain mono text. Selecting a token
 *   opens its detail card; selecting it again clears.
 * - **Aggregate** (the resolved node carries `aggregate`, i.e. a figure or
 *   metric) — the aggregate-node card. It renders every field the read model
 *   actually supplies: the evaluation horizon with its **assumed** label, the
 *   aggregate formula when one exists, the contributing streams with their real
 *   values, cadence and child formulas, and the answered known inputs. The
 *   computed value is not repeated — the modal header's `.rv-mval` carries it.
 *   An explicit placeholder is used where the aggregate formula genuinely
 *   does not exist yet (`aggregate.formula`, plan §5 #14).
 *
 * A selected value's card is the design's `.rv-drill`: identity, value, one
 * line of description, its provenance badge, and the link out to the
 * assumptions table. Source evidence and change history live on that page and
 * are intentionally omitted here to keep the modal focused on the calculation.
 *
 * Provenance uses Atlas's four-way `ProvenanceType` taxonomy, not the
 * design's three-value `type` (plan §9 #8), and a null value reads
 * "Needs input" rather than the design's "Needs decision".
 */

import { useEffect, useId, useState } from "react";
import { InfoDot } from "../data-display/InfoDot";
import type { SrcCardSource } from "../data-display/SrcCard";
import { ArrowR, ArrowUpR, X } from "../lib/icons";
import type { ProvenanceType } from "../lib/provenance";
import { effType, PROV_META, TYPE_INFO } from "../lib/provenance";
import { ReportAssumedBadge } from "./ReportAssumedBadge";
import { REPORT_EMPTY_VALUE } from "./report-format";
import type {
  OpenAssumptionHandler,
  OpenAssumptionsHandler,
  ReportTraceAggregate,
  ReportTraceContributor,
  ReportTraceKnownInput,
  ReportTraceNode,
  ReportTraceNodeKind,
  ReportTraceTarget,
} from "./report-types";

/* ------------------------------------------------------------------ *
 * Vocabulary
 * ------------------------------------------------------------------ */

/** Atlas wording for a value that has not been answered yet. */
const NEEDS_INPUT = "Needs input";

/** Tokens the design renders as plain mono glyphs rather than operands. */
const OPERATOR_TOKENS = new Set(["×", "÷", "+", "−", "-", "→", "(", ")"]);

function isOperator(token: string): boolean {
  return OPERATOR_TOKENS.has(token);
}

const KIND_LABEL: Record<ReportTraceNodeKind, string> = {
  figure: "Figure",
  metric: "Metric",
  stream: "Value stream",
  assumption: "Assumption",
  fact: "Fact",
};

/** "this figure" / "this metric" — the aggregate placeholders read as prose. */
function kindNoun(kind: ReportTraceNodeKind): string {
  return KIND_LABEL[kind].toLowerCase();
}

/**
 * Provenance pill background, keyed by `PROV_META[...].cls`.
 *
 * `empty` uses `--empty`, not the design's `--decide`. The design's vocabulary
 * calls this state "Needs decision"; Atlas deliberately diverged to "Needs
 * input" with a genuinely-missing semantic, and `--empty` is the token for a
 * missing / unfilled field. Matching the design's colour here would make this
 * the only surface in the codebase to disagree with `AssumptionDetailRail`,
 * `ProvenanceDot`, `CiteHistory` and `CiteDetail`. Plan §"Product
 * decisions" is explicit that formula traces use Atlas's provenance
 * terminology and styling.
 */
const PILL_BG: Record<string, string> = {
  prov: "bg-(--prov)",
  est: "bg-(--est)",
  cat: "bg-(--accent-plum)",
  empty: "bg-(--empty)",
};

/** `.rv-fxlbl` — a block label, a lighter weight and wider tracking. */
const FX_LABEL = [
  "flex items-baseline gap-[9px] mb-[9px]",
  "text-[10.5px] font-medium uppercase tracking-[0.09em] text-(--fg-muted)",
].join(" ");

/** `.rv-fxhint` — the inline "select any part to trace it" aside. */
const FX_HINT = "text-[11.5px] normal-case tracking-normal text-(--fg-subtle)";

/** `.rv-fx` — the bordered box the token row sits in. */
const FX_BOX = [
  "flex flex-wrap items-center gap-[7px]",
  "rounded-(--radius-container) border border-(--border-default)",
  "bg-(--bg-white) p-3.5",
].join(" ");

/* ------------------------------------------------------------------ *
 * Small parts
 * ------------------------------------------------------------------ */

/** Explicit "this field does not exist yet" note — never a fabricated value. */
function TracePlaceholder({ children }: { children: React.ReactNode }) {
  return (
    <p className="m-0 rounded-(--radius-sm) border border-dashed border-(--border-default) bg-(--bg-canvas) px-2.5 py-2 text-[11.5px] leading-[1.5] text-(--fg-muted)">
      {children}
    </p>
  );
}

/** One labelled block of the trace — the design stacks these down `.rv-mbody`. */
function TraceBlock({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-[18px] first:mt-0">
      <div className={FX_LABEL}>
        <span>{label}</span>
        {hint && <span className={FX_HINT}>{hint}</span>}
      </div>
      {children}
    </section>
  );
}

/** `.rv-dkind` — the neutral "what kind of node is this" chip. */
function KindBadge({ kind }: { kind: ReportTraceNodeKind }) {
  return (
    <span className="inline-flex items-center rounded-(--radius-xs) bg-(--bg-hover) px-[7px] py-[3px] text-[9.5px] font-bold uppercase tracking-[0.08em] text-(--fg-secondary)">
      {KIND_LABEL[kind]}
    </span>
  );
}

/**
 * Two inputs share this label, so the token that named it cannot say which one
 * it meant. The chip replaces the kind badge: on an ambiguous node even the
 * kind is a guess.
 */
function AmbiguousBadge() {
  return (
    <span className="inline-flex items-center rounded-(--radius-xs) bg-(--decide-bg) px-[7px] py-[3px] text-[9.5px] font-bold uppercase tracking-[0.08em] text-(--decide)">
      Ambiguous
    </span>
  );
}

function ProvenanceBadge({
  provenance,
  sources,
}: {
  provenance: ProvenanceType;
  sources: SrcCardSource[];
}) {
  const type = effType({ type: provenance, sources }, false);
  const meta = PROV_META[type];
  const info = TYPE_INFO[type];
  return (
    <span
      className={[
        "inline-flex items-center gap-1.5 rounded-full px-[9px] py-[3px]",
        "text-[10.5px] font-bold text-white",
        PILL_BG[meta.cls] ?? "bg-(--est)",
      ].join(" ")}
    >
      <meta.icon size={12} />
      {meta.label}
      {info && <InfoDot text={info} onDark />}
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * Tokenised formula
 * ------------------------------------------------------------------ */

interface FormulaTokensProps {
  formula: string[];
  nodes: Record<string, ReportTraceNode>;
  selectedId: string | null;
  onToggle: (nodeId: string) => void;
}

/**
 * `.rv-fx` — operands become `.rv-tok` buttons showing the node's label and
 * value; operators stay plain mono text, matching `FormulaButton`. A token with
 * no matching node is a literal baked into the formula (the design does this
 * with `"× 5 years"`), so it renders as a dashed, non-interactive chip rather
 * than pretending to be traceable.
 */
function FormulaTokens({
  formula,
  nodes,
  selectedId,
  onToggle,
}: FormulaTokensProps) {
  return (
    <div className="flex flex-wrap items-center gap-[7px]">
      {formula.map((token, index) => {
        const key = `${token}-${index}`;

        if (isOperator(token)) {
          return (
            <span
              key={key}
              className="px-0.5 font-[family-name:var(--font-mono)] text-[14px] font-bold text-(--fg-muted)"
            >
              {token}
            </span>
          );
        }

        const node = nodes[token];
        if (!node) {
          return (
            <span
              key={key}
              title="A literal written into the formula — there is no traceable value behind it."
              className="inline-flex items-baseline rounded-(--radius-field) border border-dashed border-(--border-default) bg-(--bg-canvas) px-2.5 py-1.5 text-[13px] font-medium text-(--fg-muted)"
            >
              {token}
            </span>
          );
        }

        const selected = selectedId === node.id;
        return (
          <button
            key={key}
            type="button"
            aria-pressed={selected}
            onClick={() => onToggle(node.id)}
            className={[
              "inline-flex items-baseline gap-[7px]",
              "rounded-(--radius-field) border px-2.5 py-1.5",
              "text-[13px] font-medium text-(--fg-primary) cursor-pointer",
              "underline decoration-(--border-strong) underline-offset-[3px]",
              "transition-colors duration-300 ease-in-out",
              selected
                ? "border-(--fg-brand) bg-(--bg-sky) shadow-[inset_0_0_0_1px_var(--fg-brand)]"
                : "border-(--border-default) bg-(--bg-canvas) hover:border-(--border-strong) hover:bg-(--bg-sky)",
            ].join(" ")}
          >
            <span>{node.label}</span>
            <span
              className={[
                "font-[family-name:var(--font-mono)] text-[12px] font-bold",
                node.value === null
                  ? "text-(--empty)"
                  : selected
                    ? "text-(--fg-brand)"
                    : "text-(--fg-secondary)",
              ].join(" ")}
            >
              {nodeValueLabel(node)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * An ambiguous node has no value to show — showing one input's number under a
 * label two inputs share is exactly the failure the flag exists to prevent —
 * and it is not "needs input" either, since both of them may be answered.
 */
function nodeValueLabel(node: ReportTraceNode): string {
  if (node.ambiguous) return REPORT_EMPTY_VALUE;
  return node.value ?? NEEDS_INPUT;
}

/* ------------------------------------------------------------------ *
 * Aggregate-node body
 * ------------------------------------------------------------------ */

interface ContributingStreamsProps {
  contributors: ReportTraceContributor[];
  /**
   * The expression already shown in the block above, if any. A single-stream
   * aggregate's formula *is* that stream's formula, and printing it twice would
   * put the same operand on screen as two separate controls.
   */
  rootFormula: string[] | null;
  nodes: Record<string, ReportTraceNode>;
  selectedId: string | null;
  onToggle: (nodeId: string) => void;
}

function sameFormula(a: string[], b: string[] | null): boolean {
  return b !== null && a.length === b.length && a.every((t, i) => t === b[i]);
}

/**
 * The design's `.rv-fx` box always holds one expression. Our aggregates often
 * have none (§5 #14): ROI and payback are rolled up from value streams rather
 * than recorded as a formula, so the streams carry the breakdown instead, each
 * with its own traceable tokens.
 */
function ContributingStreams({
  contributors,
  rootFormula,
  nodes,
  selectedId,
  onToggle,
}: ContributingStreamsProps) {
  return (
    <ul className="m-0 grid list-none gap-2 p-0">
      {contributors.map((contributor) => {
        const node = nodes[contributor.id];
        const selected = selectedId === contributor.id;
        return (
          <li
            key={contributor.id}
            className="rounded-(--radius-container) border border-(--border-default) bg-(--bg-white) px-3.5 py-3"
          >
            <div className="flex items-baseline justify-between gap-3">
              {node ? (
                <button
                  type="button"
                  aria-pressed={selected}
                  onClick={() => onToggle(contributor.id)}
                  className={[
                    "min-w-0 truncate text-left text-[13px] font-medium cursor-pointer",
                    selected
                      ? "text-(--fg-brand) underline underline-offset-2"
                      : "text-(--fg-primary) hover:underline hover:underline-offset-2",
                  ].join(" ")}
                >
                  {contributor.label}
                </button>
              ) : (
                <span className="min-w-0 truncate text-[13px] font-medium text-(--fg-primary)">
                  {contributor.label}
                </span>
              )}
              <div className="flex shrink-0 items-baseline gap-2">
                <span className="font-[family-name:var(--font-mono)] text-[12.5px] font-semibold text-(--fg-primary)">
                  {contributor.value}
                </span>
                <span className="text-[11px] font-medium text-(--fg-muted)">
                  {contributor.cadence === "annual"
                    ? "per year"
                    : contributor.cadence === "one-time-with-rollout"
                      ? "as rollout progresses"
                      : "one time"}
                </span>
              </div>
            </div>
            {contributor.formula.length > 0 &&
              !sameFormula(contributor.formula, rootFormula) && (
                <div className="mt-2 flex flex-wrap items-center gap-[7px]">
                  <FormulaTokens
                    formula={contributor.formula}
                    nodes={nodes}
                    selectedId={selectedId}
                    onToggle={onToggle}
                  />
                </div>
              )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The answered inputs behind an aggregate, as `.rv-tok`-style chips.
 *
 * A chip is a button when its input resolves to a trace node and plain text
 * when it does not — the two summary roll-ups (`Annual benefit` / `Annual
 * cost`) have no node of their own, so they state their value without offering
 * a drill-in that would open an empty card.
 */
function KnownInputs({
  inputs,
  nodes,
  selectedId,
  onToggle,
}: {
  inputs: ReportTraceKnownInput[];
  nodes: Record<string, ReportTraceNode>;
  selectedId: string | null;
  onToggle: (nodeId: string) => void;
}) {
  return (
    <ul className="m-0 flex list-none flex-wrap gap-[7px] p-0">
      {inputs.map((input) => {
        const node = nodes[input.id];
        const selected = selectedId === input.id;
        const shell = [
          "inline-flex items-baseline gap-[7px]",
          "rounded-(--radius-field) border px-2.5 py-1.5",
          "text-[12.5px] font-medium text-(--fg-primary)",
        ].join(" ");
        const value = (
          <span className="font-[family-name:var(--font-mono)] text-[12px] font-bold text-(--fg-secondary)">
            {input.value}
          </span>
        );
        return (
          <li key={input.id}>
            {node ? (
              <button
                type="button"
                aria-pressed={selected}
                onClick={() => onToggle(input.id)}
                className={[
                  shell,
                  "cursor-pointer transition-colors duration-300 ease-in-out",
                  "underline decoration-(--border-strong) underline-offset-[3px]",
                  selected
                    ? "border-(--fg-brand) bg-(--bg-sky) shadow-[inset_0_0_0_1px_var(--fg-brand)]"
                    : "border-(--border-default) bg-(--bg-canvas) hover:border-(--border-strong) hover:bg-(--bg-sky)",
                ].join(" ")}
              >
                <span>{input.label}</span>
                {value}
              </button>
            ) : (
              <span
                className={[
                  shell,
                  "border-(--border-default) bg-(--bg-canvas)",
                ].join(" ")}
              >
                <span>{input.label}</span>
                {value}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

interface AggregateBlocksProps {
  kind: ReportTraceNodeKind;
  aggregate: ReportTraceAggregate;
  /** The formula the modal resolved for this target, `null` when none exists. */
  formula: string[] | null;
  nodes: Record<string, ReportTraceNode>;
  selectedId: string | null;
  onToggle: (nodeId: string) => void;
}

/**
 * What an aggregate node is built from, in the order the design stacks it:
 * horizon, how it is calculated, what feeds it, and the answered inputs behind
 * those. Each block renders its real data when there is any and an explicit
 * placeholder when there is not.
 */
function AggregateBlocks({
  kind,
  aggregate,
  formula,
  nodes,
  selectedId,
  onToggle,
}: AggregateBlocksProps) {
  const traceable = formula
    ? formulaHasTraceable(formula, nodes)
    : contributorsHaveTraceable(aggregate.contributors, nodes);

  /**
   * Known inputs that are not already on screen as a formula token.
   *
   * A metric's known inputs *are* its streams' operands — `knownInputsFor` in
   * the adapter derives them from `ReportStreamRow.inputIds`, the same ids the
   * tokens come from — so rendering both would print every chip twice and give
   * two controls the same name. A figure's are summary roll-ups
   * ("Annual benefit", "Annual cost") that appear nowhere else, so they always
   * survive this. The block is dropped only when everything in it is visible
   * one block up; when the aggregate genuinely has none, the placeholder above
   * says so.
   */
  const shownTokens = new Set<string>([
    ...(formula ?? []),
    ...aggregate.contributors.flatMap((contributor) => contributor.formula),
  ]);
  const unshownInputs = aggregate.knownInputs.filter(
    (input) => !shownTokens.has(input.id),
  );

  return (
    <>
      <TraceBlock label="Evaluation horizon">
        {aggregate.horizonLabel ? (
          <div className="flex items-center gap-2">
            <span className="font-[family-name:var(--font-mono)] text-[14px] font-bold text-(--fg-primary)">
              {aggregate.horizonLabel}
            </span>
            {aggregate.horizonAssumed && (
              <ReportAssumedBadge horizonLabel={aggregate.horizonLabel} />
            )}
          </div>
        ) : (
          <TracePlaceholder>
            No evaluation horizon is recorded for this {kindNoun(kind)} yet.
          </TracePlaceholder>
        )}
      </TraceBlock>

      <TraceBlock
        label="How it is calculated"
        hint={formula && traceable ? "select any part to trace it" : undefined}
      >
        {formula ? (
          <div className={FX_BOX}>
            <FormulaTokens
              formula={formula}
              nodes={nodes}
              selectedId={selectedId}
              onToggle={onToggle}
            />
          </div>
        ) : (
          <TracePlaceholder>
            No formula is recorded for this {kindNoun(kind)} yet
            {aggregate.contributors.length > 0
              ? ", so the contributing value streams below are what it is built from."
              : "."}
          </TracePlaceholder>
        )}
      </TraceBlock>

      <TraceBlock
        label="Contributing value streams"
        hint={!formula && traceable ? "select any part to trace it" : undefined}
      >
        {aggregate.contributors.length > 0 ? (
          <ContributingStreams
            contributors={aggregate.contributors}
            rootFormula={formula}
            nodes={nodes}
            selectedId={selectedId}
            onToggle={onToggle}
          />
        ) : (
          <TracePlaceholder>
            No value stream feeds this {kindNoun(kind)} yet.
          </TracePlaceholder>
        )}
      </TraceBlock>

      {aggregate.knownInputs.length === 0 ? (
        <TraceBlock label="Known inputs">
          <TracePlaceholder>
            No answered input feeds this {kindNoun(kind)} yet.
          </TracePlaceholder>
        </TraceBlock>
      ) : (
        unshownInputs.length > 0 && (
          <TraceBlock label="Known inputs">
            <KnownInputs
              inputs={unshownInputs}
              nodes={nodes}
              selectedId={selectedId}
              onToggle={onToggle}
            />
          </TraceBlock>
        )
      )}
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Node detail card
 * ------------------------------------------------------------------ */

interface NodeDetailCardProps {
  node: ReportTraceNode;
  onOpenAssumptions?: OpenAssumptionsHandler;
}

/**
 * `.rv-drill` — the panel a selected token opens: name and its type, the value
 * with its unit, one line of description, the provenance badge, and the link
 * out to the assumptions table for source evidence and change history.
 */
function NodeDetailCard({ node, onOpenAssumptions }: NodeDetailCardProps) {
  const needsInput = node.value === null && !node.ambiguous;

  return (
    <section
      aria-label={`Trace detail for ${node.label}`}
      className="rounded-(--radius-container) border border-(--border-default) bg-(--bg-white) p-4"
    >
      <header>
        {/* .rv-dtop — name and its badges on one line. */}
        <div className="flex flex-wrap items-center gap-2.5">
          <h3 className="m-0 text-[14.5px] font-semibold text-(--fg-primary)">
            {node.label}
          </h3>
          {node.ambiguous ? (
            <AmbiguousBadge />
          ) : (
            <>
              <KindBadge kind={node.kind} />
              {node.provenance ? (
                <ProvenanceBadge
                  provenance={node.provenance}
                  sources={node.sources}
                />
              ) : (
                // §5 #15 — aggregate nodes carry no provenance of their own.
                <span className="text-[11px] text-(--fg-muted)">
                  No provenance recorded
                </span>
              )}
            </>
          )}
        </div>
        {/* .rv-dval + .rv-dunit */}
        <div className="mt-2 flex items-baseline">
          <span
            className={[
              "font-[family-name:var(--font-mono)] text-[20px] font-bold",
              needsInput ? "text-(--empty)" : "text-(--fg-primary)",
            ].join(" ")}
          >
            {nodeValueLabel(node)}
          </span>
          {node.unit && !node.ambiguous && (
            <span className="ml-2 text-[11.5px] font-medium text-(--fg-muted)">
              {node.unit}
            </span>
          )}
        </div>
      </header>

      {node.description && (
        <p className="mb-0 mt-1.5 text-[12.5px] leading-[1.5] text-(--fg-secondary)">
          {node.description}
        </p>
      )}

      {onOpenAssumptions && (
        // `.rv-dgo` — a quiet text link, not a footer button.
        <button
          type="button"
          className={[
            "group mt-3.5 inline-flex cursor-pointer items-center gap-1.5",
            "border-0 bg-transparent p-0",
            "text-[12px] font-medium text-(--fg-muted)",
            "transition-colors duration-300 ease-in-out",
            "hover:text-(--fg-brand)",
          ].join(" ")}
          onClick={onOpenAssumptions}
        >
          Open in the assumptions table
          <span className="text-(--fg-subtle) group-hover:text-(--fg-brand)">
            <ArrowR size={12} />
          </span>
        </button>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * Selectability
 * ------------------------------------------------------------------ */

function formulaHasTraceable(
  formula: string[] | null,
  nodes: Record<string, ReportTraceNode>,
): boolean {
  if (!formula) return false;
  return formula.some((token) => !isOperator(token) && nodes[token] != null);
}

/** Contributors are traceable through their own node or through their tokens. */
function contributorsHaveTraceable(
  contributors: ReportTraceContributor[],
  nodes: Record<string, ReportTraceNode>,
): boolean {
  return contributors.some(
    (contributor) =>
      nodes[contributor.id] != null ||
      formulaHasTraceable(contributor.formula, nodes),
  );
}

function knownInputsHaveTraceable(
  inputs: ReportTraceKnownInput[],
  nodes: Record<string, ReportTraceNode>,
): boolean {
  return inputs.some((input) => nodes[input.id] != null);
}

/* ------------------------------------------------------------------ *
 * Modal
 * ------------------------------------------------------------------ */

export interface FormulaTraceModalProps {
  target: ReportTraceTarget;
  nodes: Record<string, ReportTraceNode>;
  onClose: () => void;
  onOpenAssumptions: OpenAssumptionsHandler;
  /** Reserved; not wired this pass. */
  onOpenAssumption?: OpenAssumptionHandler;
}

/**
 * `DrillModal` — the read-only trace behind a figure, metric or value stream.
 *
 * `onOpenAssumption` is intentionally accepted and unused: it is the future
 * focused-row deep link (plan §8 #4), kept on the props so Phase 7 does not
 * have to reshape the interface later.
 */
export function FormulaTraceModal({
  target,
  nodes,
  onClose,
  onOpenAssumptions,
}: FormulaTraceModalProps) {
  const instanceId = useId();
  const titleId = `formula-trace-${instanceId}-title`;

  // Keyed by target so switching rows clears the selection without an effect.
  const [selection, setSelection] = useState<{
    targetId: string;
    nodeId: string;
  } | null>(null);
  const selectedId =
    selection && selection.targetId === target.id ? selection.nodeId : null;

  function toggle(nodeId: string) {
    setSelection((prev) =>
      prev && prev.targetId === target.id && prev.nodeId === nodeId
        ? null
        : { targetId: target.id, nodeId },
    );
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const targetNode = nodes[target.id] ?? null;
  const aggregate = target.aggregate ?? targetNode?.aggregate ?? null;

  /**
   * One formula for the whole modal. The target carries it when the row that
   * opened this knows it; otherwise the resolved aggregate may. Only when
   * neither does (§5 #14) do the contributing streams stand in.
   */
  const rootFormula = target.formula ?? aggregate?.formula ?? null;

  const selectedNode = selectedId === null ? null : (nodes[selectedId] ?? null);
  const rootHasTraceable = aggregate
    ? formulaHasTraceable(rootFormula, nodes) ||
      contributorsHaveTraceable(aggregate.contributors, nodes) ||
      knownInputsHaveTraceable(aggregate.knownInputs, nodes)
    : formulaHasTraceable(rootFormula, nodes);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center font-[family-name:var(--font-body)]">
      {/* Scrim */}
      <button
        type="button"
        aria-label="Close formula trace"
        className="absolute inset-0 border-0 bg-[rgba(8,15,28,0.42)]"
        onClick={onClose}
      />

      {/* Card */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative z-[1] flex h-auto max-h-[88vh] w-[720px] max-w-[92vw] flex-col overflow-hidden rounded-(--radius-container) bg-(--bg-canvas) shadow-[0_24px_60px_-16px_rgba(0,0,0,0.4)]"
      >
        {/* `.rv-mhead` */}
        <header className="flex flex-none items-start gap-[14px] border-b border-(--border-subtle) bg-(--bg-white) px-5 py-[18px]">
          <div className="min-w-0">
            <h2
              id={titleId}
              className="m-0 text-[13px] font-medium text-(--fg-muted)"
            >
              {target.label}
            </h2>
            <div className="mt-[3px] font-[family-name:var(--font-mono)] text-[26px] font-bold tracking-[-0.02em] text-(--fg-primary)">
              {target.value}
            </div>
          </div>
          <button
            type="button"
            aria-label="Close formula trace"
            className="ml-auto inline-flex h-[26px] w-[26px] flex-none cursor-pointer items-center justify-center rounded-full border-0 bg-transparent text-(--fg-muted) hover:bg-(--bg-hover) hover:text-(--fg-primary)"
            onClick={onClose}
          >
            <X size={18} />
          </button>
        </header>

        {/* `.rv-mbody` — the aggregate blocks or the design's single token row,
            then either the hint, the solo link, or the selected value's
            panel. */}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-[18px]">
          {aggregate ? (
            <AggregateBlocks
              kind={target.kind}
              aggregate={aggregate}
              formula={rootFormula}
              nodes={nodes}
              selectedId={selectedId}
              onToggle={toggle}
            />
          ) : (
            <TraceBlock
              label="How it is calculated"
              hint={
                rootHasTraceable ? "select any part to trace it" : undefined
              }
            >
              {rootFormula ? (
                <div className={FX_BOX}>
                  <FormulaTokens
                    formula={rootFormula}
                    nodes={nodes}
                    selectedId={selectedId}
                    onToggle={toggle}
                  />
                </div>
              ) : (
                <TracePlaceholder>
                  No formula is recorded for this value yet.
                </TracePlaceholder>
              )}
            </TraceBlock>
          )}

          {selectedNode ? (
            <div className="mt-[18px]">
              <NodeDetailCard
                node={selectedNode}
                onOpenAssumptions={onOpenAssumptions}
              />
            </div>
          ) : rootHasTraceable ? (
            // `.rv-hint`
            <p className="m-0 mt-3.5 flex items-center gap-2 text-[12.5px] leading-[1.5] text-(--fg-muted)">
              <span className="shrink-0 text-(--fg-subtle)">
                <ArrowUpR size={13} />
              </span>
              Select any part above to see where it comes from and open it in
              the assumptions table.
            </p>
          ) : (
            onOpenAssumptions && (
              // `.rv-dgo.solo` — nothing to trace, so the link is the only way on.
              <button
                type="button"
                className={[
                  "group mt-4 inline-flex cursor-pointer items-center gap-1.5",
                  "border-0 bg-transparent p-0",
                  "text-[12px] font-medium text-(--fg-muted)",
                  "transition-colors duration-300 ease-in-out",
                  "hover:text-(--fg-brand)",
                ].join(" ")}
                onClick={onOpenAssumptions}
              >
                Open in the assumptions table
                <span className="text-(--fg-subtle) group-hover:text-(--fg-brand)">
                  <ArrowR size={12} />
                </span>
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
}
